import { useEffect, useMemo, useRef, useState } from 'react';
import {
  motion,
  inView,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from 'framer-motion';
import type { Transition } from 'framer-motion';
import type { CalculatedLine, Point } from './connections';
import { buildGeometry, arcLengthAtY, pointAt, focusY } from './geometry';
import { markRevealed, isRevealed } from './revealStore';
import { useReducedMotionPref } from './useReducedMotionPref';

interface ConnectionLayerProps {
  lines: CalculatedLine[];
}

const NODE_RADIUS = 3.5;
const TIP_RADIUS = 4;
const GLOW_RADIUS = 14;
/** Arc length (px) over which the scroll tip fades out as it reaches the end. */
const TIP_FADE_DISTANCE = 24;

function getSVGPath(line: CalculatedLine): string {
  const { points, path } = line;
  if (points.length < 2) return '';

  // Multi-point lines are always drawn as a polyline
  if (points.length > 2 || path === 'straight') {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  }

  const { x: x1, y: y1 } = points[0]!;
  const { x: x2, y: y2 } = points[1]!;

  switch (path) {
    case 'curved': {
      // Bezier curve with control point at midpoint
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;

      // Offset control point perpendicular to line
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      const offset = len * 0.2; // 20% of line length

      const perpX = -dy / len;
      const perpY = dx / len;

      const ctrlX = midX + perpX * offset;
      const ctrlY = midY + perpY * offset;

      return `M ${x1} ${y1} Q ${ctrlX} ${ctrlY} ${x2} ${y2}`;
    }

    case 'stepped': {
      // Manhattan/stepped path (horizontal then vertical, or vice versa)
      const midX = (x1 + x2) / 2;
      // Go horizontal to midpoint, then vertical to end
      return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
    }

    default:
      return `M ${x1} ${y1} L ${x2} ${y2}`;
  }
}

function nodePoints(line: CalculatedLine): Point[] {
  const first = line.points[0];
  const last = line.points[line.points.length - 1];
  if (!first || !last) return [];
  switch (line.node) {
    case 'start':
      return [first];
    case 'end':
      return [last];
    case 'both':
      return [first, last];
    default:
      return [];
  }
}

/** Timeline node dots at a line's configured ends; they pop in when the line starts. */
function NodeDots({
  line,
  visible,
  reduced,
}: {
  line: CalculatedLine;
  visible: boolean;
  reduced: boolean;
}) {
  return (
    <>
      {nodePoints(line).map((p, i) => (
        <motion.circle
          key={i}
          cx={p.x}
          cy={p.y}
          fill={line.style.nodeColor}
          initial={reduced ? false : { r: 0, opacity: 0 }}
          animate={visible ? { r: NODE_RADIUS, opacity: 0.95 } : { r: 0, opacity: 0 }}
          transition={{ duration: reduced ? 0 : 0.3, ease: 'easeOut' }}
        />
      ))}
    </>
  );
}

function strokeProps(line: CalculatedLine) {
  return {
    stroke: line.style.color,
    strokeWidth: line.style.width,
    strokeDasharray: line.style.dashArray,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    fill: 'none',
  };
}

/** Draws once on mount after its delay (the original behaviour). */
function LoadLine({ line, reduced }: { line: CalculatedLine; reduced: boolean }) {
  const { delay, duration, ease } = line.animation;
  const { reveals } = line;

  useEffect(() => {
    if (!reveals) return;
    if (reduced) {
      markRevealed(reveals);
      return;
    }
    const timer = window.setTimeout(() => markRevealed(reveals), delay * 1000);
    return () => window.clearTimeout(timer);
  }, [reveals, delay, reduced]);

  return (
    <g>
      <motion.path
        d={getSVGPath(line)}
        {...strokeProps(line)}
        initial={reduced ? false : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: line.style.opacity }}
        transition={
          reduced
            ? { duration: 0 }
            : ({
                pathLength: { delay, duration, ease },
                opacity: { delay, duration: 0.01 },
              } as Transition)
        }
      />
      <NodeDots line={line} visible reduced={reduced} />
    </g>
  );
}

/** Draws once when its watched element scrolls into view. */
function InViewLine({ line, reduced }: { line: CalculatedLine; reduced: boolean }) {
  // If this line's card was already revealed (e.g. we remounted after a
  // breakpoint change), draw straight away so card and branch stay in step.
  const [started, setStarted] = useState(() => reduced || isRevealed(line.reveals));
  const mountedAt = useRef(performance.now());
  const { delay, duration, ease } = line.animation;
  const { reveals } = line;
  const { watch, margin } = line.inView;

  useEffect(() => {
    if (reduced) {
      if (reveals) markRevealed(reveals);
      return;
    }
    if (isRevealed(reveals)) return;
    if (!watch || !document.querySelector(watch)) {
      setStarted(true);
      if (reveals) markRevealed(reveals);
      return;
    }

    let timer = 0;
    let fired = false;
    const start = () => {
      setStarted(true);
      if (reveals) markRevealed(reveals);
    };
    // Fire once. `delay` is the earliest start after mount so lines that are
    // already in view on load can wait for the spine to reach them.
    const fire = () => {
      if (fired) return;
      fired = true;
      stop();
      window.removeEventListener('scroll', onScroll);
      const earliest = mountedAt.current + delay * 1000;
      const wait = Math.max(0, earliest - performance.now());
      timer = window.setTimeout(start, wait);
    };
    // Elements near the bottom of the page may never cross the in-view line
    // (the page can't scroll that far), so reveal whatever is left at page end.
    const atPageEnd = () =>
      window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
    const onScroll = () => atPageEnd() && fire();
    const stop = inView(watch, fire, {
      margin: margin as `${number}px ${number}px ${number}% ${number}px`,
      amount: 'some',
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    if (atPageEnd()) fire();

    return () => {
      stop();
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(timer);
    };
  }, [watch, margin, delay, reveals, reduced]);

  return (
    <g>
      <motion.path
        d={getSVGPath(line)}
        {...strokeProps(line)}
        initial={reduced ? false : { pathLength: 0, opacity: 0 }}
        animate={
          started
            ? { pathLength: 1, opacity: line.style.opacity }
            : { pathLength: 0, opacity: 0 }
        }
        transition={
          reduced
            ? { duration: 0 }
            : ({ pathLength: { duration, ease }, opacity: { duration: 0.01 } } as Transition)
        }
      />
      <NodeDots line={line} visible={started} reduced={reduced} />
    </g>
  );
}

/**
 * Path length bound to scroll progress: the line draws as you scroll and a
 * glowing tip rides the end, sitting a little below where you're reading. The
 * tip fades out as it arrives at the end of the path.
 */
function ScrollLine({ line, reduced }: { line: CalculatedLine; reduced: boolean }) {
  const geometry = useMemo(() => buildGeometry(line.points), [line.points]);
  const { focus } = line.scroll;
  const { delay } = line.animation;
  const [active, setActive] = useState(false);

  const { scrollY } = useScroll();
  const raw = useMotionValue(0);
  // The spring gives the on-load draw and lets horizontal jogs sweep instead of pop
  const smooth = useSpring(raw, { stiffness: 300, damping: 40, restDelta: 0.5, restSpeed: 2 });

  useEffect(() => {
    if (reduced) return;

    // Viewport metrics are cached here; this effect re-runs whenever the lines
    // are re-measured (resize, fonts, content changes), which refreshes them.
    let vh = window.innerHeight;
    let maxScroll = Math.max(0, document.documentElement.scrollHeight - vh);
    const recompute = () => {
      const y = focusY(scrollY.get(), vh, maxScroll, focus, geometry.endY);
      raw.set(arcLengthAtY(geometry, y));
    };

    let released = false;
    const timer = window.setTimeout(() => {
      released = true;
      setActive(true);
      recompute();
    }, delay * 1000);
    const unsubscribe = scrollY.on('change', () => released && recompute());
    const onResize = () => {
      vh = window.innerHeight;
      maxScroll = Math.max(0, document.documentElement.scrollHeight - vh);
      if (released) recompute();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.clearTimeout(timer);
      unsubscribe();
      window.removeEventListener('resize', onResize);
    };
  }, [geometry, focus, delay, reduced, scrollY, raw]);

  const progress = useTransform(smooth, (s) =>
    geometry.total > 0 ? Math.min(1, Math.max(0, s / geometry.total)) : 1
  );
  const tipX = useTransform(smooth, (s) => pointAt(geometry, s).x);
  const tipY = useTransform(smooth, (s) => pointAt(geometry, s).y);
  const endFade = useTransform(smooth, (s) =>
    Math.min(1, Math.max(0, (geometry.total - s) / TIP_FADE_DISTANCE))
  );

  const d = getSVGPath(line);

  if (reduced) {
    // Fully drawn; the tip would have faded out at the end anyway
    return (
      <g>
        <path d={d} {...strokeProps(line)} opacity={line.style.opacity} />
        <NodeDots line={line} visible reduced />
      </g>
    );
  }

  const glowId = `spine-glow-${line.id}`;

  return (
    <g>
      <defs>
        {/* Soft halo for the tip; a gradient repaints far cheaper than a blur filter */}
        <radialGradient id={glowId}>
          <stop offset="0%" stopColor={line.style.glowColor} stopOpacity="0.9" />
          <stop offset="45%" stopColor={line.style.glowColor} stopOpacity="0.35" />
          <stop offset="100%" stopColor={line.style.glowColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      <motion.path
        d={d}
        {...strokeProps(line)}
        opacity={line.style.opacity}
        style={{ pathLength: progress }}
      />
      {/* Outer group: appears once the spine starts. Inner group: fades out at the end. */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.g style={{ opacity: endFade }}>
          <motion.circle
            cx={tipX}
            cy={tipY}
            fill={`url(#${glowId})`}
            initial={{ opacity: 0.9, r: GLOW_RADIUS }}
            animate={{ opacity: [0.9, 0.5, 0.9], r: [GLOW_RADIUS, GLOW_RADIUS * 1.35, GLOW_RADIUS] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx={tipX} cy={tipY} r={TIP_RADIUS} fill={line.style.tipColor} />
        </motion.g>
      </motion.g>
      <NodeDots line={line} visible={active} reduced={false} />
    </g>
  );
}

export function ConnectionLayer({ lines }: ConnectionLayerProps) {
  const reduced = useReducedMotionPref();

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 5 }}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {lines.map((line) => {
        switch (line.trigger) {
          case 'scroll':
            return <ScrollLine key={line.id} line={line} reduced={reduced} />;
          case 'in-view':
            return <InViewLine key={line.id} line={line} reduced={reduced} />;
          default:
            return <LoadLine key={line.id} line={line} reduced={reduced} />;
        }
      })}
    </svg>
  );
}
